import { TaskInfo } from '../models/types';

export type LngLat = [number, number];
export type RouteGeometryArcMap = Map<string, LngLat[]>;

export interface RouteGeometryTripSegments {
  exact: LngLat[][];
  fallback: LngLat[][];
}

export type RouteGeometryByTrip = Record<string, RouteGeometryTripSegments>;

const arcKey = (fromNodeKey: string, toNodeKey: string): string => `${fromNodeKey}\u0000${toNodeKey}`;

export function decodePolyline6(encoded: string): LngLat[] {
  const coordinates: LngLat[] = [];
  let index = 0;
  let latitude = 0;
  let longitude = 0;

  const decodeValue = (): number => {
    let result = 0;
    let shift = 0;
    while (index < encoded.length) {
      const value = encoded.charCodeAt(index++) - 63;
      if (value < 0) {
        throw new Error('Invalid encoded polyline');
      }
      result |= (value & 0x1f) << shift;
      shift += 5;
      if (value < 0x20) {
        return (result & 1) ? ~(result >> 1) : (result >> 1);
      }
    }
    throw new Error('Truncated encoded polyline');
  };

  while (index < encoded.length) {
    latitude += decodeValue();
    longitude += decodeValue();
    coordinates.push([longitude / 1_000_000, latitude / 1_000_000]);
  }
  return coordinates;
}

export function parseRouteGeometryCsv(csv: string): RouteGeometryArcMap {
  const rows = parseCsvRows(csv);
  if (rows.length === 0) {
    return new Map();
  }
  const header = rows[0].map((value, index) => index === 0 ? value.replace(/^\uFEFF/, '') : value);
  const fromIndex = header.indexOf('fromNodeKey');
  const toIndex = header.indexOf('toNodeKey');
  const geometryIndex = header.indexOf('geometryPolyline6');
  if (fromIndex < 0 || toIndex < 0 || geometryIndex < 0) {
    throw new Error('Route geometry CSV has an unsupported header');
  }

  const arcs: RouteGeometryArcMap = new Map();
  rows.slice(1).forEach((row) => {
    const fromNodeKey = row[fromIndex]?.trim();
    const toNodeKey = row[toIndex]?.trim();
    const encoded = row[geometryIndex]?.trim();
    if (!fromNodeKey || !toNodeKey || !encoded) {
      return;
    }
    try {
      const coordinates = decodePolyline6(encoded);
      if (coordinates.length >= 2) {
        arcs.set(arcKey(fromNodeKey, toNodeKey), coordinates);
      }
    } catch {
      // One malformed arc must not prevent the rest of the sparse artifact from loading.
    }
  });
  return arcs;
}

export function buildRouteGeometryByTrip(
  taskInfo: TaskInfo,
  arcs: RouteGeometryArcMap
): RouteGeometryByTrip {
  const sourceNodeKey = taskInfo.config.sourceRouteGeometryNodeKey;
  if (!sourceNodeKey) {
    return {};
  }

  return Object.fromEntries(taskInfo.trips.map((trip) => {
    const points = [...trip.tpDetails]
      .filter((point) => point.routeGeometryNodeKey)
      .sort((left, right) => left.seqNo - right.seqNo);
    const nodes = [
      { nodeKey: sourceNodeKey, lngLat: taskInfo.config.sourceLngLat },
      ...points.map((point) => ({ nodeKey: point.routeGeometryNodeKey!, lngLat: point.lngLat })),
      { nodeKey: sourceNodeKey, lngLat: taskInfo.config.sourceLngLat },
    ];
    const exact: LngLat[][] = [];
    const fallback: LngLat[][] = [];

    for (let index = 0; index < nodes.length - 1; index += 1) {
      const from = nodes[index];
      const to = nodes[index + 1];
      const geometry = arcs.get(arcKey(from.nodeKey, to.nodeKey));
      if (geometry && geometry.length >= 2) {
        appendStitchedLine(exact, geometry);
      } else if (isLngLat(from.lngLat) && isLngLat(to.lngLat)) {
        fallback.push([from.lngLat, to.lngLat]);
      }
    }
    return [trip.name, { exact, fallback }];
  }));
}

function appendStitchedLine(lines: LngLat[][], segment: LngLat[]): void {
  const current = lines[lines.length - 1];
  if (current && sameCoordinate(current[current.length - 1], segment[0])) {
    current.push(...segment.slice(1));
    return;
  }
  lines.push([...segment]);
}

function sameCoordinate(left: LngLat, right: LngLat): boolean {
  return Math.abs(left[0] - right[0]) < 1e-9 && Math.abs(left[1] - right[1]) < 1e-9;
}

function isLngLat(value: unknown): value is LngLat {
  return Array.isArray(value)
    && value.length >= 2
    && Number.isFinite(Number(value[0]))
    && Number.isFinite(Number(value[1]));
}

function parseCsvRows(csv: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;

  for (let index = 0; index < csv.length; index += 1) {
    const char = csv[index];
    if (quoted) {
      if (char === '"' && csv[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else if (char === '"') {
        quoted = false;
      } else {
        cell += char;
      }
    } else if (char === '"' && cell.length === 0) {
      quoted = true;
    } else if (char === ',') {
      row.push(cell);
      cell = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && csv[index + 1] === '\n') {
        index += 1;
      }
      row.push(cell);
      if (row.some((value) => value.length > 0)) {
        rows.push(row);
      }
      row = [];
      cell = '';
    } else {
      cell += char;
    }
  }
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    if (row.some((value) => value.length > 0)) {
      rows.push(row);
    }
  }
  return rows;
}
